import ImageCache from "@services/wheel/ImageCache.js";
import PieSlice from "./PieSlice.js";
import * as FontPicker from "./FontPicker.js";
import * as hubSizes from "./hubSizes.js";
import * as Util from "@utils/Util.js";
import * as ImageUtil from "@utils/ImageUtil.js";

export default class WheelPainter {
  constructor() {
    this.imageCache = new ImageCache();
    this.entries = [];
    this.imageDataCache = {};
  }

  draw(context, angle, displayEntries, allEntries, wheelConfig, darkMode) {
    // CRITICAL: Clear the entire canvas first to prevent accumulation
    context.clearRect(0, 0, context.canvas.width, context.canvas.height);

    this.angle = angle;
    this.displayEntries = displayEntries;
    this.allEntries = allEntries;
    this.wheelConfig = wheelConfig;
    this.wheelRadius = context.canvas.width * 0.44;
    this.hubRadius = this.getHubRadius(
      wheelConfig.type,
      this.wheelRadius,
      wheelConfig.hubSize
    );

    // Use CSS variables for theme colors if possible
    let darkModeColor = "#1c1917"; // Default dark background
    try {
      // Try to get computed value from CSS variable
      const computedStyle = getComputedStyle(document.documentElement);
      darkModeColor =
        computedStyle.getPropertyValue("--md-background").trim() ||
        darkModeColor;
    } catch (e) {
      console.error("Error getting CSS variable:", e);
    }

    this.backgroundColor = darkMode
      ? darkModeColor
      : wheelConfig.pageBackgroundColor;
    this.drawShadows = false; // Don't draw any shadows
    this.drawBackgroundColor(context);
    this.drawWheelShadow(context);
    this.drawCoverImage(context);
    this.drawWheel(context);
    this.drawPointer(context);
    this.drawCenterImage(context);
    this.drawCoverPlate(context);
  }

  getHubRadius(wheelType, wheelRadius, hubSize) {
    if (wheelType == "image") return 0;
    const hubFraction = hubSizes.hubSizes[hubSize] || 0.2;
    return Math.round(hubFraction * wheelRadius);
  }

  refresh() {
    // Clear all cached images to prevent accumulation
    this.wheelImage = null;
    this.pointerImage = null;
    this.coverPlateImage = null;
    this.wheelShadowImage = null;

    // Clear the image data cache completely
    this.imageDataCache = {};

    // Clear any cached entries array
    this.entries = [];

    // Force refresh of the main image cache if it has a clear method
    if (this.imageCache && typeof this.imageCache.clear === "function") {
      this.imageCache.clear();
    }
  }

  drawBackgroundColor(context) {
    // Skip drawing background color entirely to allow transparency
    return;
  }

  drawWheelShadow(context) {
    if (!this.drawShadows) return;
    if (!this.wheelShadowImage) {
      this.wheelShadowImage = ImageUtil.createInMemoryImage(context);
      this.drawWheelShadowNoCache(this.wheelShadowImage.getContext("2d"));
    }
    context.drawImage(this.wheelShadowImage, 0, 0);
  }

  drawWheelShadowNoCache(context) {
    const x = context.canvas.width / 2;
    const y = context.canvas.height / 2;

    // Create a soft shadow effect that matches the wheel size
    context.save();

    // Create a large enough shadow area
    const shadowWidth = this.wheelRadius * 0.15; // Shadow width as proportion of wheel radius

    // Use a more gradual gradient with multiple stops for softer edges
    const gradient = context.createRadialGradient(
      x,
      y,
      this.wheelRadius - 1, // Start slightly inside the wheel edge
      x,
      y,
      this.wheelRadius + shadowWidth
    );

    // Multiple stops for smoother transition
    gradient.addColorStop(0, "rgba(128, 128, 128, 0.5)"); // Start with medium opacity
    gradient.addColorStop(0.2, "rgba(128, 128, 128, 0.4)"); // Reduce opacity gradually
    gradient.addColorStop(0.5, "rgba(128, 128, 128, 0.25)"); // Continue reducing
    gradient.addColorStop(0.8, "rgba(128, 128, 128, 0.1)"); // Very subtle
    gradient.addColorStop(1, "rgba(128, 128, 128, 0)"); // Fade to transparent

    // Draw the shadow
    context.beginPath();
    context.arc(x, y, this.wheelRadius + shadowWidth, 0, Math.PI * 2);
    context.fillStyle = gradient;
    context.fill();

    // Clear the wheel area to make it transparent
    context.globalCompositeOperation = "destination-out";
    context.beginPath();
    context.arc(x, y, this.wheelRadius, 0, Math.PI * 2);
    context.fill();

    context.restore();
  }

  drawWheel(context) {
    if (!this.wheelImage) {
      this.wheelImage = ImageUtil.createInMemoryImage(context);
      this.entries = this.displayEntries.slice(0);
      this.drawWheelNoCache(this.wheelImage.getContext("2d"));
    }
    const width = context.canvas.width;
    const height = context.canvas.height;
    context.save();
    context.translate(width / 2, height / 2);
    context.rotate(this.angle);
    context.translate(-width / 2, -height / 2);
    context.drawImage(this.wheelImage, 0, 0);
    context.restore();
  }

  drawWheelNoCache(context) {
    this.drawSlices(context);
    this.drawCenterCircle(context);
  }

  drawCoverImage(context) {
    if (this.wheelConfig.type == "color") return;
    const image = this.imageCache.getImage(this.wheelConfig.getCoverImage());
    if (!image) return;
    context.save();
    context.translate(context.canvas.width / 2, context.canvas.height / 2);
    context.rotate(this.angle);
    context.beginPath();
    context.arc(0, 0, this.wheelRadius - 1, 0, Math.PI * 2, true);
    context.clip();
    context.drawImage(
      image,
      -this.wheelRadius,
      -this.wheelRadius,
      this.wheelRadius * 2,
      this.wheelRadius * 2
    );
    context.restore();
  }

  drawSlices(context) {
    context.save();
    context.translate(context.canvas.width / 2, context.canvas.height / 2);
    const self = this;
    if (this.wheelConfig.isAdvanced) {
      const totalWeight = Util.getTotalWeight(this.entries);
      const radians = [];
      let smallestAngle = 2 * Math.PI;
      this.entries.forEach((entry, index) => {
        radians[index] = (2 * Math.PI * entry.weight) / totalWeight;
        if (radians[index] < smallestAngle) smallestAngle = radians[index];
      });
      context.font = FontPicker.getFont(
        context,
        this.allEntries.map((e) => e.text),
        this.wheelRadius,
        this.hubRadius,
        smallestAngle
      );
      this.displayEntries
        .map(function (entry, index) {
          return new PieSlice(
            self.wheelConfig,
            radians[index],
            self.wheelRadius,
            self.hubRadius,
            index,
            entry,
            self.getImageFromData(entry.image)
          );
        })
        .forEach((slice, index) => {
          const radiansPerSegment = radians[index] / 2 + radians[index + 1] / 2;
          slice.draw(context);
          context.rotate(-radiansPerSegment);
        });
    } else {
      const radiansPerSegment = (2 * Math.PI) / this.displayEntries.length;
      context.font = FontPicker.getFont(
        context,
        this.allEntries.map((e) => e.text),
        this.wheelRadius,
        this.hubRadius,
        radiansPerSegment
      );
      this.displayEntries
        .map(function (entry, index) {
          return new PieSlice(
            self.wheelConfig,
            radiansPerSegment,
            self.wheelRadius,
            self.hubRadius,
            index,
            entry,
            self.getImageFromData(entry.image)
          );
        })
        .forEach((slice) => {
          slice.draw(context);
          context.rotate(-radiansPerSegment);
        });
    }
    context.restore();
  }

  drawCenterCircle(context) {
    if (this.wheelConfig.type == "image") return;
    context.save();
    context.translate(context.canvas.width / 2, context.canvas.height / 2);
    context.fillStyle = "white";
    context.beginPath();
    context.arc(0, 0, this.hubRadius, 0, Math.PI * 2);
    context.fill();
    if (this.wheelConfig.drawOutlines) {
      context.lineWidth = 2;
      context.strokeStyle = "#333333";
      context.stroke();
    }
    context.restore();
  }

  drawPointer(context) {
    if (!this.pointerImage) {
      this.pointerImage = ImageUtil.createInMemoryImage(context);
      this.drawPointerNoCache(this.pointerImage.getContext("2d"));
    }
    context.drawImage(this.pointerImage, 0, 0);
  }

  drawPointerNoCache(context) {
    context.save();
    context.translate(context.canvas.width / 2, context.canvas.height / 2);
    if (this.drawShadows) {
      context.shadowColor = "rgba(128, 128, 128, 0.6)";
      context.shadowOffsetY = 4;
      context.shadowBlur = 15;
    }
    context.beginPath();
    context.moveTo(this.wheelRadius - 15, 0);
    context.lineTo(this.wheelRadius + 25, -20);
    context.lineTo(this.wheelRadius + 25, 20);
    context.lineTo(this.wheelRadius - 15, 0);
    if (this.wheelConfig.drawOutlines) {
      context.lineWidth = 2;
      context.strokeStyle = "#333333";
      context.stroke();
    }
    context.fillStyle = "#BBB";
    context.fill();
    context.restore();
  }

  drawCoverPlate(context) {
    if (this.displayEntries.length == this.allEntries.length) return;
    if (!this.coverPlateImage && this.displayEntries.length > 0) {
      this.coverPlateImage = ImageUtil.createInMemoryImage(context);
      this.drawCoverPlateNoCache(this.coverPlateImage.getContext("2d"));
    }
    if (this.coverPlateImage) {
      context.drawImage(this.coverPlateImage, 0, 0);
    }
  }

  drawCoverPlateNoCache(context) {
    const entriesCount = this.displayEntries.length;
    const radians = Math.max((2 * 2 * Math.PI) / entriesCount, Math.PI / 4);
    context.save();
    context.translate(context.canvas.width / 2, context.canvas.height / 2);
    context.scale(-1, 1);
    if (this.drawShadows) {
      context.shadowColor = "rgba(128, 128, 128, 0.6)";
      context.shadowOffsetY = 4;
      context.shadowBlur = 15;
    }
    context.lineWidth = this.wheelRadius - this.hubRadius + 10;
    const image = this.imageCache.getImage("/images/brushed-metal.png");
    context.strokeStyle = context.createPattern(image, "repeat");
    context.beginPath();
    context.arc(
      0,
      0,
      (this.wheelRadius + this.hubRadius) / 2,
      -radians / 2,
      radians / 2
    );
    context.stroke();
    context.restore();
  }

  drawCenterImage(context) {
    // The center image is not drawn as part of drawWheelNoCache() because it
    // takes a few ticks to load.
    if (this.wheelConfig.type == "image") return;
    const image = this.imageCache.getImage(this.wheelConfig.getWheelImage());
    if (!image) return;
    context.save();
    context.translate(context.canvas.width / 2, context.canvas.height / 2);
    context.rotate(this.angle);
    context.beginPath();
    context.arc(0, 0, this.hubRadius - 1, 0, Math.PI * 2, true);
    context.clip();
    context.drawImage(
      image,
      -this.hubRadius,
      -this.hubRadius,
      this.hubRadius * 2,
      this.hubRadius * 2
    );
    context.restore();
  }

  getImageFromData(imageData) {
    if (imageData) {
      if (!this.imageDataCache[imageData]) {
        const image = new Image();
        const self = this;
        image.onload = function () {
          self.clearCachedWheelImage();
        };
        image.setAttribute("crossOrigin", "anonymous");
        image.src = imageData;
        this.imageDataCache[imageData] = image;
      }
      return this.imageDataCache[imageData];
    }
  }

  clearCachedWheelImage() {
    this.wheelImage = null;
  }
}
